import { IListResponse, XHRInstance } from "@sembark-travel/xhr"
import { TCustomerPaymentPreference } from "../CustomerPaymentPreferences"
import type {
	TPolicyHead,
	TPolicyVersionBase,
	TPolicyVersionSummary,
} from "../shared/policies"

// A rule block: the payment rules that apply for a check-in date range. The
// base block, which applies whenever no date range matches, has no dates.
export type TPaymentRuleSet = {
	id: number
	valid_from: string | null
	valid_to: string | null
	rules: Array<TCustomerPaymentPreference>
}

export type TPaymentPolicy = TPolicyVersionBase & {
	rule_sets: Array<TPaymentRuleSet>
}

export type TPaymentPolicyVersion = TPolicyVersionSummary

export type TPaymentPolicyDetail = TPolicyHead<TPaymentPolicy>

// Payload Type. The rules themselves are managed as customer payment
// preferences, so a policy only carries their ids.
export type TPaymentPolicyPayload = {
	name: string
	additional_info?: string
	rule_sets: Array<{
		valid_from: string | null
		valid_to: string | null
		rules: Array<number>
	}>
}

export function paymentPoliciesXHR(xhr: XHRInstance) {
	return {
		async get(params?: Record<string, unknown>) {
			return await xhr
				.get<
					IListResponse<TPaymentPolicyDetail>
				>("/payment-policy-heads", { params })
				.then((resp) => resp.data)
		},

		async create(
			payload: Omit<TPaymentPolicyPayload, "name"> & { name?: string }
		): Promise<TPaymentPolicyDetail> {
			return xhr
				.post<{
					data: TPaymentPolicyDetail
				}>("/payment-policy-heads", payload)
				.then((resp) => resp.data.data)
		},

		async update(
			detailId: number,
			payload: Omit<TPaymentPolicyPayload, "name">
		): Promise<TPaymentPolicyDetail | { message: string }> {
			return xhr
				.put<{
					data: TPaymentPolicyDetail | { message: string }
				}>(`/payment-policy-heads/${detailId}`, payload)
				.then((resp) => resp.data.data)
		},

		async disable(detailId: number): Promise<void> {
			await xhr.delete(`/payment-policy-heads/${detailId}`)
		},

		async restore(detailId: number): Promise<void> {
			await xhr.patch(`/payment-policy-heads/${detailId}/restore`)
		},

		async getVersions(id: number, params?: Record<string, unknown>) {
			return await xhr
				.get<
					IListResponse<TPaymentPolicyVersion>
				>(`/payment-policy-heads/${id}/payment-policies`, { params })
				.then((resp) => resp.data)
		},

		async getVersionById(id: number): Promise<TPaymentPolicy> {
			return xhr
				.get<{ data: TPaymentPolicy }>(`/payment-policies/${id}`)
				.then((resp) => resp.data.data)
		},
	}
}
